import { factionOf, type PlayerView } from '@bm/shared';
import { de } from '@/i18n/de';
import { avatarColor } from '@/lib/avatar';

interface PlayerCardProps {
  player: PlayerView;
  isSelf: boolean;
  /** Optionales Badge zusätzlich zu Host/Du/Offline — z. B. "hat gewählt". */
  trailing?: React.ReactNode;
}

/**
 * Zeigt Status niemals nur über Farbe (GDD §33, §39): Host, Du, Offline und
 * Ausgeschieden sind zusätzlich als Text/Icon vorhanden.
 */
export function PlayerCard({ player, isSelf, trailing }: PlayerCardProps) {
  const initial = player.name.charAt(0).toUpperCase();

  return (
    <div
      className={`flex items-center gap-3 rounded-xl border border-surface-border bg-surface px-3 py-2.5 ${
        !player.alive ? 'opacity-50 saturate-50' : ''
      }`}
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-sm font-bold text-black/80"
        style={{ background: avatarColor(player.name) }}
        aria-hidden="true"
      >
        {initial}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 truncate">
          <span className={`truncate font-medium ${!player.alive ? 'line-through' : ''}`}>
            {player.name}
          </span>
          {player.isHost && <span title={de.lobby.host}>👑</span>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
          {isSelf && <Badge>{de.lobby.you}</Badge>}
          {!player.connected && <Badge>{de.lobby.offline}</Badge>}
          {!player.alive && player.role && (
            <Badge tone={factionOf(player.role) === 'MAFIA' ? 'mafia' : 'citizen'}>
              {de.roles[player.role]}
            </Badge>
          )}
        </div>
      </div>

      {trailing}
    </div>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: 'mafia' | 'citizen';
}) {
  const toneClass =
    tone === 'mafia'
      ? 'bg-mafia-muted text-mafia'
      : tone === 'citizen'
        ? 'bg-citizen-muted text-citizen'
        : 'bg-surface-2 text-fg-muted';

  return (
    <span className={`rounded-full px-2 py-0.5 font-medium ${toneClass}`}>{children}</span>
  );
}
